import React, { Component } from "react";
import { connect } from "react-redux";
import withStyles from "@material-ui/core/styles/withStyles";
import UserActions from "../../actions/user";
import { GlobalState } from "../../types/globalState";
import {
  Button,
  createStyles,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Theme,
  Typography,
  WithStyles,
} from "@material-ui/core";
import { showSnackbar } from "../SharedComponents/Notifier";
import LinearProgress from "@material-ui/core/LinearProgress";
import { hideDialog } from "../SharedComponents/Dialog";
import Selector, { Option } from "../SharedComponents/Selector";
import { validateEmail } from "../../utils/validator";
import Messages from "../../consts/Messages";
import { TextInput } from "../SharedComponents/TextInput";
import { filter } from "../../utils/profanityFilter";
import { Gender, Rules } from "../../types/user";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateUtils from "@date-io/date-fns";
import SearchClub from "../Search/SearchClub";

interface Props extends WithStyles {
  insertNewUser(data: any): void;
  updateUser(id: string, data: any): void;
  edit?: any;
  loading: boolean;
  isPlayer?: boolean;
}
interface State {
  user: any;
  err: any;
  err_msg: any;
}

class UserSetup extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    let user = {
      rule: Rules.Player,
      name: "",
      lastName: "",
      email: "",
      username: "",
      password: "",
      gender: Gender.Men,
      hcp: "",
      cdh: "",
      socialHcp: "",
    };
    this.state = {
      user,
      err: {
        name: false,
        lastName: false,
        email: false,
        username: false,
        password: false,
        hcp: false,
        cdh: false,
        socialHcp: false,
      },
      err_msg: {
        name: "",
        lastName: "",
        email: "",
        username: "",
        password: "",
        hcp: "",
        cdh: "",
        socialHcp: "",
      },
    };
  }

  clearForm = () => {};

  _validateSocialHcp = (socialHcpValue: string | undefined): { msg?: string; socialHcp?: boolean } => {
    if (!socialHcpValue || socialHcpValue.trim() === "") {
      return {}; // Not an error if empty, handled by mutual exclusivity or if optional
    }
    const hcpRegex = /^[-+]?\d{1,2}(?:\.\d)?$/; // Allows numbers like -2.1, 10, 18.5
    const value = parseFloat(socialHcpValue);
    if (!hcpRegex.test(socialHcpValue) || isNaN(value) || value < -5.0 || value > 54.0) {
      return { msg: "Invalid Social Handicap (e.g., -5.0 to 54.0)", socialHcp: true };
    }
    return {};
  };

  _validateWHSBoxes = (cdhNumber: string = "", handicapIndex: string = ""): { msg?: string; cdh?: boolean; hcp?: boolean } => {
    // If both are empty, no WHS-specific error from this function.
    // The overall form validation will decide if any handicap is needed at all.
    if ((!cdhNumber || cdhNumber.trim() === "") && (!handicapIndex || handicapIndex.trim() === "")) {
      return {};
    }

    const hcpRegex = /^[-+]?\d{1,2}(?:\.\d)?$/; // Allows numbers like -2.1, 10, 18.5
    const cdhRegex = /^\d{1,10}$/; // CDH is usually numeric, 1 to 10 digits.

    // If one is filled, the other must be filled too
    if ((cdhNumber && cdhNumber.trim() !== "") && (!handicapIndex || handicapIndex.trim() === "")) {
      return { msg: "Handicap Index is required when CDH is provided", hcp: true };
    }
    if ((!cdhNumber || cdhNumber.trim() === "") && (handicapIndex && handicapIndex.trim() !== "")) {
      return { msg: "CDH is required when Handicap Index is provided", cdh: true };
    }

    // If both are intended to be filled (i.e., not empty), validate their formats
    if (cdhNumber && !cdhRegex.test(cdhNumber.trim())) {
      return { msg: "Invalid CDH number format (must be 1-10 digits)", cdh: true };
    }

    if (handicapIndex) {
      const hcpValue = parseFloat(handicapIndex.trim());
      if (!hcpRegex.test(handicapIndex.trim()) || isNaN(hcpValue) || hcpValue < -5.0 || hcpValue > 54.0) {
        return { msg: "Invalid Handicap Index (e.g., -5.0 to 54.0)", hcp: true };
      }
    }
    return {}; // Both are validly formatted (or both were empty initially)
  };

  formValidation = () => {
    const { name, lastName, email, username, password, hcp, cdh, socialHcp } = this.state.user;
    let err: any = {
      name: false,
      lastName: false,
      email: false,
      username: false,
      password: false,
      hcp: false,
      cdh: false,
      socialHcp: false,
    };
    let err_msg: any = {
      name: "",
      lastName: "",
      email: "",
      username: "",
      password: "",
      hcp: "",
      cdh: "",
      socialHcp: "",
    };

    // Basic field validations
    if (!name || filter.isProfane(name)) {
      err.name = true;
      err_msg.name = "Name is required and should not contain profanity.";
    }
    if (!lastName || filter.isProfane(lastName)) {
      err.lastName = true;
      err_msg.lastName = "Last name is required and should not contain profanity.";
    }
    if (!validateEmail(email)) {
      err.email = true;
      err_msg.email = Messages.error.emailNotValid;
    }
    if (username.length < 5 || filter.isProfane(username)) {
      err.username = true;
      err_msg.username = Messages.error.usernameShortLength;
    }
    // Password validation might only be for new users. Assuming it's always required here.
    if (password.length < 6) {
      err.password = true;
      err_msg.password = Messages.error.passwordShortLength;
    }

    const cdhValue = cdh ? cdh.trim() : "";
    const hcpValue = hcp ? hcp.trim() : "";
    const socialHcpValue = socialHcp ? socialHcp.trim() : "";

    const hasWHSInput = cdhValue !== "" || hcpValue !== "";
    const hasSocialHcpInput = socialHcpValue !== "";

    if (hasWHSInput && hasSocialHcpInput) {
      err.hcp = true;
      err.cdh = true;
      err.socialHcp = true;
      // Apply a general message to one of the fields, or a shared error display area if you have one.
      err_msg.socialHcp = "Use WHS (CDH & Handicap Index) OR Social Handicap, not both.";
      // Optionally, clear other specific messages for these fields or set them to indicate the conflict
      err_msg.hcp = "Conflict with Social Handicap";
      err_msg.cdh = "Conflict with Social Handicap";
    } else if (hasWHSInput) {
      const whsErr = this._validateWHSBoxes(cdhValue, hcpValue);
      if (whsErr.cdh) {
        err.cdh = true;
        err_msg.cdh = whsErr.msg;
      }
      if (whsErr.hcp) {
        err.hcp = true;
        // Display message on hcp field as well, if it's part of the error
        // or if cdh error message doesn't cover it.
        err_msg.hcp = whsErr.msg;
      }
    } else if (hasSocialHcpInput) {
      const socialErr = this._validateSocialHcp(socialHcpValue);
      if (socialErr.socialHcp) {
        err.socialHcp = true;
        err_msg.socialHcp = socialErr.msg;
      }
    }
    // If no handicap is entered, and it's optional, no error is set here.
    // If at least one form of handicap is mandatory, you would add a check:
    // if (!hasWHSInput && !hasSocialHcpInput) { ... set error ... }

    this.setState({ err, err_msg });

    return !(err.name || err.lastName || err.email || err.username || err.password || err.hcp || err.cdh || err.socialHcp);
  };

  _handleTextInputChange = (value: string, name: string) => {
    this.setState({
      user: { ...this.state.user, [name]: value },
    });
  };
  _handleTextFieldChange = (e: React.ChangeEvent<{ name: string; value: any }>) => {
    this.setState({
      user: { ...this.state.user, [e.target.name]: e.target.value },
    });
  };
  _handleRuleChange = (rule: string) => {
    this.setState({
      user: {
        ...this.state.user,
        rule,
      },
    });
  };
  _submitForm = () => {
    const user = this.state.user;
    const newUser = {
      ...user,
      name: user.name.trim(),
      lastName: user.lastName.trim(),
      email: user.email.trim(),
      username: user.username.trim(),
      dob: Date.parse(user.dob?.toString()),
    };
    if (this.formValidation()) {
      this.props.insertNewUser(newUser);
    } else {
      showSnackbar("Form is not valid, check items");
    }
  };

  renderActionButton() {
    const { classes, loading } = this.props;
    if (loading)
      return (
        <div className={classes.loadingContainer}>
          <LinearProgress color="primary" variant={"indeterminate"} />
        </div>
      );
    else
      return (
        <div className={classes.actionButtonContainer}>
          <Button
            fullWidth
            className={classes.actionButton}
            variant={"contained"}
            color={"primary"}
            onClick={this._submitForm}
          >
            Save
          </Button>
          <Button
            fullWidth
            className={classes.actionButton}
            variant={"contained"}
            color={"secondary"}
            onClick={hideDialog}
          >
            Cancel
          </Button>
        </div>
      );
  }

  render() {
    const { classes } = this.props;
    const { name, lastName, username, email, password, rule, dob, gender, cdh = "", homeClub, hcp = "", socialHcp = "" } = this.state.user;
    const { err, err_msg } = this.state;

    const cdhTrimmed = cdh.trim();
    const hcpTrimmed = hcp.trim();
    const socialHcpTrimmed = socialHcp.trim();

    const whsFieldsDisabled = socialHcpTrimmed !== "";
    const socialHcpFieldDisabled = cdhTrimmed !== "" || hcpTrimmed !== "";

    const options: Option[] = [
      { value: Rules.Admin, label: "Admin" },
      { value: Rules.Organiser, label: "Organiser" },
      { value: Rules.ClubManager, label: "Club Manager" },
      { value: Rules.Player, label: "Player" },
    ];
    return (
      <Paper className={classes.paper}>
        <Typography variant={"h5"} className={classes.title}>
          User Setup
        </Typography>
        <FormControl margin="none" color="primary" required fullWidth>
          <TextInput
            id="name"
            name="name"
            label={"Name"}
            autoComplete="name"
            value={name}
            error={err.name}
            helperText={err_msg.name}
            captalize
            onChange={this._handleTextInputChange}
          />
        </FormControl>
        <FormControl margin="none" color="primary" required fullWidth>
          <TextInput
            id="lastName"
            name="lastName"
            label={"Last Name"}
            autoComplete="lastName"
            value={lastName}
            error={err.lastName}
            helperText={err_msg.lastName}
            captalize
            onChange={this._handleTextInputChange}
          />
        </FormControl>
        <FormControl margin="none" color="primary" required fullWidth>
          <TextInput
            id="email"
            name="email"
            label={"Email"}
            autoComplete="disabled"
            value={email}
            error={err.email}
            helperText={err_msg.email}
            disableProfanityCheck
            onChange={this._handleTextInputChange}
          />
        </FormControl>
        <FormControl margin="none" color="primary" required fullWidth>
          <TextInput
            id="username"
            name="username"
            autoComplete="disabled"
            label={"Username"}
            value={username}
            error={err.username}
            helperText={err_msg.username}
            onChange={this._handleTextInputChange}
          />
        </FormControl>
        <FormControl margin="none" color="primary" required fullWidth>
          <TextInput
            id="password"
            name="password"
            type="password"
            label={"Password"}
            value={password}
            error={err.password}
            helperText={err_msg.password}
            autoComplete="new-password"
            disableProfanityCheck
            onChange={this._handleTextInputChange}
          />
        </FormControl>
        <FormControl color="primary" size={"medium"} fullWidth style={{ marginTop: 16 }}>
          <FormLabel component="legend">Gender</FormLabel>
          <RadioGroup defaultValue="men" aria-label="gender" name="gender" onChange={this._handleTextFieldChange} row>
            <FormControlLabel value="men" control={<Radio />} label="Male" />
            <FormControlLabel value="women" control={<Radio />} label="Female" />
          </RadioGroup>
        </FormControl>
        <FormControl color="primary" size={"medium"} fullWidth>
          <TextField
            type="text"
            name="hcp"
            label={"Handicap Index"}
            style={{ margin: "3px 0" }}
            value={hcp}
            error={err.hcp}
            helperText={err_msg.hcp}
            // inputProps={{ min: -5, max: 54 }} // Adjusted range, though TextField type="text" doesn't enforce it. Validation does.
            onChange={this._handleTextFieldChange}
            disabled={whsFieldsDisabled}
          />
        </FormControl>
        <FormControl color="primary" size={"medium"} fullWidth>
          <TextField
            type="text" // Changed from number to text for better control with regex validation
            label={"CDH ID No."}
            style={{ margin: "3px 0" }}
            value={cdh}
            error={err.cdh}
            helperText={err_msg.cdh}
            name="cdh"
            onChange={this._handleTextFieldChange}
            disabled={whsFieldsDisabled}
          />
        </FormControl>
        <FormControl color="primary" size={"medium"} fullWidth>
          <TextField
            type="text"
            name="socialHcp"
            label={"Social Handicap"}
            style={{ margin: "3px 0" }}
            value={socialHcp}
            error={err.socialHcp}
            helperText={err_msg.socialHcp}
            onChange={this._handleTextFieldChange}
            disabled={socialHcpFieldDisabled}
          />
        </FormControl>
        <MuiPickersUtilsProvider utils={DateUtils}>
          <KeyboardDatePicker
            id="date"
            variant="dialog"
            format="dd/MM/yyyy"
            margin="normal"
            fullWidth
            placeholder="Date of birth"
            value={dob || null}
            onChange={(date: Date | null) => this.setState(state => ({ user: { ...state.user, dob: date } }))}
          />
        </MuiPickersUtilsProvider>
        <SearchClub
          style={{ marginTop: 8 }}
          dialog
          placeHolder="Tap To Search Home Club"
          onClubSelected={(homeClub: any) => this.setState(state => ({ user: { ...state.user, homeClub } }))}
        />
        {!this.props.isPlayer && (
          <Selector variant={"outlined"} onChange={this._handleRuleChange} value={rule} options={options} />
        )}
        {this.renderActionButton()}
      </Paper>
    );
  }
}

const styles = (theme: Theme) =>
  createStyles({
    title: {
      fontWeight: "bold",
    },
    paper: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      position: "relative",
      left: "50%",
      transform: "translateX(-50%)",
      width: "auto",
      backgroundColor: "rgba(255,255,552,0.85)",
      padding: theme.spacing(2),
    },
    inline: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    actionButtonContainer: {
      display: "flex",
      width: "100%",
    },
    actionButton: {
      marginTop: theme.spacing(1),
    },
    loadingContainer: {
      width: "100%",
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(2),
    },
  });
const mapStateToProps = (state: GlobalState) => {
  const { loading } = state.general;
  return {
    loading,
  };
};

export default connect(mapStateToProps, {
  insertNewUser: UserActions.insert,
  updateUser: UserActions.updateMe,
})(withStyles(styles)(UserSetup));
